import { useCallback, useMemo, useRef, useState } from 'react';

import { AddOutlined, ChevronRight } from '@mui/icons-material';
import { Box, ClickAwayListener, Paper, Popper, Typography } from '@mui/material';

// ============================================================================
// Constants
// ============================================================================

const NEW_DASHBOARD_KEY = '__new_dashboard__';

// ============================================================================
// Types
// ============================================================================

export type TabInfo = {
  tabId: string;
  tabName: string;
};

export type CreateDashboardResult =
  | { ok: true; dashboardName: string }
  | { ok: false; reason?: string; message?: string };

export type DashboardTargetMenuVariant = 'with-tabs' | 'dashboard-only';

export interface DashboardTargetMenuProps {
  anchorEl: HTMLElement | null;
  open: boolean;
  onClose: () => void;

  /**
   * Variant of the menu:
   * - 'with-tabs': Shows dashboard list with tab submenu on hover (default)
   * - 'dashboard-only': Shows only dashboard list without tab submenu
   */
  variant?: DashboardTargetMenuVariant;

  /**
   * Current tab ID to exclude from the list (when variant is 'with-tabs')
   */
  excludeCurrentTabId?: string | null;

  /**
   * Whether to show "New page" option in the submenu
   */
  showNewPageOption?: boolean;

  /**
   * Whether to show chevron icon on dashboard items
   */
  showChevron?: boolean;

  /**
   * Called when a tab is selected (variant 'with-tabs')
   */
  onSelectTab?: (dashboardName: string, tab: TabInfo) => void;

  /**
   * Called when "New page" is selected (variant 'with-tabs')
   */
  onCreateNewTab?: (dashboardName: string) => void;

  /**
   * Called when a dashboard is selected (variant 'dashboard-only')
   */
  onSelectDashboard?: (dashboardName: string) => void;

  /**
   * Optional filter for tab names (return true to include)
   */
  filterTab?: (dashboardName: string, tabName: string, tabId: string) => boolean;

  /**
   * Optional custom empty state message
   */
  emptyMessage?: string;

  /**
   * Whether to disable all interactions
   */
  disabled?: boolean;

  /**
   * Callback for creating a new dashboard draft.
   * Should call context.createDashboardDraft and return the result.
   */
  onCreateDashboard?: () => CreateDashboardResult | Promise<CreateDashboardResult | null> | null;

  /**
   * List of dashboard names to display.
   */
  dashboards: string[];

  /**
   * Function to get tabs for a dashboard.
   */
  getTabs: (dashboardName: string) => TabInfo[] | null;

  /**
   * Current dashboard name to highlight/sort.
   */
  currentDashboardName?: string | null;

  /**
   * Callback for errors during dashboard creation
   */
  onError?: (message: string) => void;
}

// ============================================================================
// Styles
// ============================================================================

const MENU_ITEM_HEIGHT = 36;
const MENU_OFFSET = [0, 0];

const menuItemStyles = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  px: 1,
  minHeight: MENU_ITEM_HEIGHT,
  cursor: 'pointer',
  transition: 'background-color 0.15s ease',
  '&:hover': {
    backgroundColor: 'rgba(255, 255, 255, 0.08)'
  }
} as const;

const activeMenuItemStyles = {
  ...menuItemStyles,
  backgroundColor: 'rgba(255, 255, 255, 0.08)'
} as const;

const disabledMenuItemStyles = {
  ...menuItemStyles,
  cursor: 'not-allowed',
  opacity: 0.6,
  '&:hover': {
    backgroundColor: 'inherit'
  }
} as const;

const menuItemTextStyles = {
  fontSize: '12px',
  fontWeight: 600
} as const;

// ============================================================================
// Component
// ============================================================================

export const DashboardTargetMenu = ({
  anchorEl,
  open,
  onClose,
  variant = 'with-tabs',
  excludeCurrentTabId: _excludeCurrentTabId,
  showNewPageOption = true,
  showChevron = true,
  onSelectTab,
  onCreateNewTab,
  onSelectDashboard,
  filterTab: _filterTab,
  emptyMessage = 'No other dashboards',
  disabled = false,
  onCreateDashboard,
  dashboards,
  getTabs,
  currentDashboardName: customCurrentDashboardName,
  onError
}: DashboardTargetMenuProps) => {
  const [hoveredDashboard, setHoveredDashboard] = useState<string | null>(null);
  const [hoveredDashboardRef, setHoveredDashboardRef] = useState<HTMLElement | null>(null);
  const dashboardItemRefs = useRef<Map<string, HTMLElement>>(new Map());
  const subMenuTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Store ref for dashboard items
  const setDashboardRef = useCallback((key: string, el: HTMLElement | null) => {
    if (el) {
      dashboardItemRefs.current.set(key, el);
    } else {
      dashboardItemRefs.current.delete(key);
    }
  }, []);

  // Update anchor element when hover changes
  const handleSetHoveredDashboard = useCallback((key: string | null) => {
    setHoveredDashboard(key);
    if (key) {
      const ref = dashboardItemRefs.current.get(key) ?? null;
      setHoveredDashboardRef(ref);
    } else {
      setHoveredDashboardRef(null);
    }
  }, []);

  // Use current dashboard name if available
  const effectiveCurrentDashboardName = customCurrentDashboardName;

  /**
   * Build dashboard list.
   * Current dashboard is sorted first for 'with-tabs' variant.
   */
  const dashboardNamesList = useMemo(() => {
    return [...dashboards].sort((a, b) => {
      if (variant === 'with-tabs') {
        if (a === effectiveCurrentDashboardName) return -1;
        if (b === effectiveCurrentDashboardName) return 1;
      }
      return a.localeCompare(b);
    });
  }, [dashboards, effectiveCurrentDashboardName, variant]);

  /**
   * Get tabs for hovered dashboard.
   */
  const hoveredDashboardTabs = useMemo(() => {
    if (!hoveredDashboard || variant === 'dashboard-only') return null;

    const tabs = getTabs(hoveredDashboard);

    // If there are no tabs but we can create a new one, return an empty array
    // so the submenu still opens and shows the "New page" option
    if (!tabs && showNewPageOption) {
      return [];
    }

    return tabs;
  }, [hoveredDashboard, variant, getTabs, showNewPageOption]);

  const handleDashboardMouseEnter = useCallback(
    (dashboardName: string) => {
      if (subMenuTimeoutRef.current) {
        clearTimeout(subMenuTimeoutRef.current);
      }
      handleSetHoveredDashboard(dashboardName);
    },
    [handleSetHoveredDashboard]
  );

  const handleDashboardMouseLeave = useCallback(() => {
    subMenuTimeoutRef.current = setTimeout(() => {
      handleSetHoveredDashboard(null);
    }, 150);
  }, [handleSetHoveredDashboard]);

  const handleSubMenuMouseEnter = useCallback(() => {
    if (subMenuTimeoutRef.current) {
      clearTimeout(subMenuTimeoutRef.current);
    }
  }, []);

  const handleSubMenuMouseLeave = useCallback(() => {
    subMenuTimeoutRef.current = setTimeout(() => {
      handleSetHoveredDashboard(null);
    }, 150);
  }, [handleSetHoveredDashboard]);

  const handleTabClick = useCallback(
    (dashboardName: string, tabId: string, tabName: string) => {
      if (disabled) return;
      onSelectTab?.(dashboardName, { tabId, tabName });
      onClose();
    },
    [disabled, onSelectTab, onClose]
  );

  const handleNewTabClick = useCallback(
    (dashboardName: string) => {
      if (disabled) return;
      onCreateNewTab?.(dashboardName);
      onClose();
    },
    [disabled, onCreateNewTab, onClose]
  );

  const handleDashboardClick = useCallback(
    (dashboardName: string) => {
      if (disabled) return;
      if (variant === 'dashboard-only') {
        onSelectDashboard?.(dashboardName);
        onClose();
      }
    },
    [disabled, variant, onSelectDashboard, onClose]
  );

  /**
   * Handle creating a new dashboard and triggering appropriate action.
   */
  const handleCreateNewDashboard = useCallback(async () => {
    if (disabled || !onCreateDashboard) return;

    const result = await onCreateDashboard();

    if (!result) return;

    if (!result.ok) {
      const message =
        result.message ||
        (result.reason
          ? `Failed to create dashboard: ${result.reason}`
          : 'Failed to create dashboard');
      onError?.(message);
      return;
    }

    const newDashboardName = result.dashboardName;
    if (!newDashboardName) {
      onError?.('Failed to create dashboard: missing name.');
      return;
    }

    if (variant === 'with-tabs') {
      if (!onCreateNewTab) {
        onError?.('Failed to create dashboard: missing page handler.');
        return;
      }
      onCreateNewTab(newDashboardName);
      onClose();
      return;
    }

    // For dashboard-only variant, select the new dashboard directly
    onSelectDashboard?.(newDashboardName);
    onClose();
  }, [disabled, onCreateDashboard, variant, onCreateNewTab, onSelectDashboard, onClose, onError]);

  return (
    <Popper
      anchorEl={anchorEl}
      modifiers={[
        {
          name: 'offset',
          options: {
            offset: MENU_OFFSET
          }
        }
      ]}
      open={open}
      placement='bottom-start'
      sx={{ zIndex: 10000 }}
    >
      <ClickAwayListener onClickAway={onClose}>
        <Paper
          sx={{
            backgroundColor: 'secondary.main',
            minWidth: 200
          }}
        >
          {/* Empty State - only shown when no dashboards AND no create option */}
          {dashboardNamesList.length === 0 && !onCreateDashboard ? (
            <Box sx={disabledMenuItemStyles}>{emptyMessage}</Box>
          ) : (
            <>
              {/* Dashboard List */}
              {dashboardNamesList.map(dashboardName => (
                <Box
                  key={dashboardName}
                  ref={(el: HTMLDivElement | null) => {
                    setDashboardRef(dashboardName, el);
                  }}
                  sx={
                    disabled
                      ? disabledMenuItemStyles
                      : hoveredDashboard === dashboardName
                        ? activeMenuItemStyles
                        : menuItemStyles
                  }
                  onClick={() => {
                    handleDashboardClick(dashboardName);
                  }}
                  onMouseEnter={() => {
                    if (variant === 'with-tabs') {
                      handleDashboardMouseEnter(dashboardName);
                    }
                  }}
                  onMouseLeave={variant === 'with-tabs' ? handleDashboardMouseLeave : undefined}
                >
                  <Typography sx={menuItemTextStyles}>{dashboardName}</Typography>
                  {showChevron && variant === 'with-tabs' && (
                    <ChevronRight sx={{ fontSize: 20, opacity: 0.7 }} />
                  )}
                </Box>
              ))}

              {/* New Dashboard Section */}
              {onCreateDashboard && (
                <Box
                  ref={(el: HTMLDivElement | null) => {
                    setDashboardRef(NEW_DASHBOARD_KEY, el);
                  }}
                  sx={disabled ? disabledMenuItemStyles : menuItemStyles}
                  onClick={handleCreateNewDashboard}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <AddOutlined sx={{ fontSize: 18 }} />
                    <Typography sx={{ ...menuItemTextStyles, color: 'text.secondary' }}>
                      New Dashboard
                    </Typography>
                  </Box>
                </Box>
              )}
            </>
          )}

          {/* Sub-menu for tabs */}
          {variant === 'with-tabs' &&
            hoveredDashboard &&
            hoveredDashboardTabs !== null &&
            hoveredDashboardRef && (
              <Popper
                open
                anchorEl={hoveredDashboardRef}
                modifiers={[
                  {
                    name: 'offset',
                    options: {
                      offset: MENU_OFFSET
                    }
                  }
                ]}
                placement='right-start'
                sx={{ zIndex: 10001 }}
              >
                <Paper
                  sx={{
                    backgroundColor: 'secondary.main',
                    minWidth: 180
                  }}
                  onMouseEnter={handleSubMenuMouseEnter}
                  onMouseLeave={handleSubMenuMouseLeave}
                >
                  {/* Tab List */}
                  {hoveredDashboardTabs.map(({ tabName, tabId }) => (
                    <Box
                      key={tabId}
                      sx={disabled ? disabledMenuItemStyles : menuItemStyles}
                      onClick={() => {
                        handleTabClick(hoveredDashboard, tabId, tabName);
                      }}
                    >
                      <Typography sx={menuItemTextStyles}>{tabName}</Typography>
                    </Box>
                  ))}

                  {/* New Tab Option */}
                  {showNewPageOption && (
                    <Box
                      sx={disabled ? disabledMenuItemStyles : menuItemStyles}
                      onClick={() => {
                        handleNewTabClick(hoveredDashboard);
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <AddOutlined sx={{ fontSize: 18 }} />
                        <Typography sx={{ ...menuItemTextStyles, color: 'text.secondary' }}>
                          New page
                        </Typography>
                      </Box>
                    </Box>
                  )}
                </Paper>
              </Popper>
            )}
        </Paper>
      </ClickAwayListener>
    </Popper>
  );
};

export default DashboardTargetMenu;
